import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ConflictRow {
  readonly queueId: string;
  readonly deviceId: string;
  readonly entityType: string;
  readonly entityRef: string;
  readonly operation: string;
  readonly status: string;
  readonly retryCount: number;
  readonly maxRetries: number;
  readonly conflictResolution: string | null;
  readonly lastError: string | null;
  readonly clientTimestamp: string;
}

/**
 * M22 L22.06 — Offline Sync Conflict Log.
 * Surfaces the offline sync queue entries that are in CONFLICT
 * (t283_offline_sync_queue) so an admin can review and resolve them.
 */
export function M22ConflictLogPage(): ReactNode {
  const { at } = useBusinessDay();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ConflictRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: ConflictRow[] }>('/v1/mobile/sync/queue?status=CONFLICT', { token })
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m22.conflictLog.loading')}</p>;
  return (
    <main aria-label="mobile-conflict-log">
      <h1>{t('m22.conflictLog.title')}</h1>
      {rows.length === 0 ? (
        <p>{t('m22.conflictLog.empty')}</p>
      ) : (
        <table aria-label="mobile-conflict-log-table">
          <thead>
            <tr><th>{t('m22.conflictLog.colEntity')}</th><th>{t('m22.conflictLog.colRef')}</th><th>{t('m22.conflictLog.colOperation')}</th><th>{t('m22.conflictLog.colStatus')}</th><th>{t('m22.conflictLog.colRetries')}</th><th>{t('m22.conflictLog.colResolution')}</th><th>{t('m22.conflictLog.colError')}</th><th>{t('m22.conflictLog.colClientTime')}</th></tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.queueId}>
                <td>{c.entityType}</td>
                <td>{c.entityRef}</td>
                <td>{c.operation}</td>
                <td>{c.status}</td>
                <td>{c.retryCount}/{c.maxRetries}</td>
                <td>{c.conflictResolution ?? '—'}</td>
                <td>{c.lastError ?? '—'}</td>
                <td>{at(c.clientTimestamp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
